import { PROGRESS_STEP } from '@/features/InvoiceReport/Edit/components/UtilizePastDetails/constants';
import { useDataContext } from '@/features/InvoiceReport/Edit/components/UtilizePastDetails/context/UtilizePastDetailsDataContext';
import { useApplyPastTransactions } from '@/features/InvoiceReport/Edit/components/UtilizePastDetails/hooks/useApplyPastTransactions';
import {
  useTransactionDetailsDrawer,
  type UseTransactionDetailsDrawerOptions,
} from '@/features/InvoiceReport/Edit/components/UtilizePastDetails/TransactionDetailsDrawer/hooks/useTransactionDetailsDrawer';
import { InvoiceTransaction } from '@/features/InvoiceReport/Edit/type';
import { DraggableDrawer, Loading } from '@moneyforward/ap-frontend-components';
import { ApPayeeDetail as ApPayeeModel } from 'ap-openapi';
import { useCallback, useMemo } from 'react';
import { Contents } from './components/Contents';
import { Footer } from './components/Contents/Footer';
import { TransactionDetailsHeader } from './components/Header';
import styles from './TransactionDetails.module.scss';

export type TransactionDetailsProps = {
  selectedPayee?: ApPayeeModel;
  onImport: (items: InvoiceTransaction[]) => void;
} & UseTransactionDetailsDrawerOptions;

export const TransactionDetailsDrawer: React.FC<TransactionDetailsProps> = ({
  selectedPayee,
  onImport,
  showColumnDetails,
  canDisplayDrExcise,
}) => {
  const {
    handleApplyPastTransactions,
    isLoading: isLoadingApplyPastTransactions,
  } = useApplyPastTransactions(onImport, {
    canDisplayTransactionDetail: showColumnDetails,
  });

  const { currentStep, onSetCurrentStep, onCancel, selectedFile } =
    useDataContext();
  // Determine invoiceId safely
  const invoiceId =
    selectedFile &&
    typeof selectedFile === 'object' &&
    'invoiceId' in selectedFile
      ? selectedFile.invoiceId
      : '';

  const { isLoading, detailProps, contentDetailProps } =
    useTransactionDetailsDrawer(invoiceId, {
      showColumnDetails,
      canDisplayDrExcise,
    });

  const open = useMemo(() => {
    return currentStep === PROGRESS_STEP.ON_OPEN_DRAWER;
  }, [currentStep]);

  const reselect = useCallback(() => {
    onSetCurrentStep(PROGRESS_STEP.ON_OPEN_DIALOG);
  }, [onSetCurrentStep]);

  return (
    <DraggableDrawer
      visible={open}
      onClose={onCancel}
      width={600}
      minWidth={400}
      mask
      title={<TransactionDetailsHeader />}
      footer={
        <Footer
          onCancel={onCancel}
          onApply={() =>
            handleApplyPastTransactions(
              selectedPayee,
              detailProps?.transactionList,
              onCancel
            )
          }
          isLoadingApplyPastTransactions={isLoadingApplyPastTransactions}
        />
      }
    >
      <div className={styles.drawerContent}>
        {isLoading ? (
          <Loading size='medium' />
        ) : (
          detailProps && (
            <Contents
              transactionDetailProps={detailProps}
              onReselect={reselect}
              contentProps={contentDetailProps}
            />
          )
        )}
      </div>
    </DraggableDrawer>
  );
};
